import React from 'react';
import { Instagram, Mail, Phone } from 'lucide-react';
import { footerData } from '../mock/spanglishData';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gradient-to-br from-purple-300 via-pink-300 to-blue-300 text-purple-800 pt-16 pb-8 border-t-4 border-purple-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div className="space-y-4">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              {footerData.companyName}
            </h3>
            <p className="text-purple-700 text-sm leading-relaxed">
              {footerData.description}
            </p>
            {/* Social Links */}
            <div className="flex gap-4 pt-2">
              <a 
                href={footerData.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-pink-200 to-pink-300 hover:from-pink-300 hover:to-pink-400 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 border-2 border-pink-400"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5 text-pink-700" />
              </a>
              <a 
                href={footerData.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-purple-200 to-purple-300 hover:from-purple-300 hover:to-purple-400 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 border-2 border-purple-400"
                aria-label="TikTok"
              >
                <svg className="h-5 w-5 text-purple-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a 
                href={footerData.social.threads}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gradient-to-br from-purple-200 to-purple-300 hover:from-purple-300 hover:to-purple-400 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 border-2 border-purple-400 overflow-hidden"
                aria-label="Threads"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_spanglish-hub/artifacts/n4jjnlde_image.png" 
                  alt="Threads"
                  className="h-7 w-7 object-contain"
                />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-bold text-purple-700 mb-4">Company</h4>
            <ul className="space-y-3">
              {footerData.links.company.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href.replace('#', ''))}
                    className="text-purple-700 hover:text-pink-600 text-sm transition-colors duration-300 font-semibold"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-bold text-purple-700 mb-4">Support</h4>
            <ul className="space-y-3">
              {footerData.links.support.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href.replace('#', ''))}
                    className="text-purple-700 hover:text-pink-600 text-sm transition-colors duration-300 font-semibold"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-purple-700 mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-pink-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <a href={`mailto:${footerData.email}`} className="text-purple-700 hover:text-pink-600 transition-colors font-semibold">
                    {footerData.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <a href={`https://wa.me/${footerData.phone.replace(/[\s+]/g, '')}`} className="text-purple-700 hover:text-pink-600 transition-colors font-semibold">
                    {footerData.phone}
                  </a>
                  <p className="text-purple-600 text-xs">WhatsApp & iMessage</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t-2 border-purple-400 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <p className="text-purple-700 text-sm font-semibold">
              {footerData.copyright}
            </p>

            {/* Legal Links */}
            <div className="flex flex-wrap gap-6 text-sm">
              {footerData.links.legal.map((link, index) => (
                <button
                  key={index}
                  onClick={() => scrollToSection(link.href.replace('#', ''))}
                  className="text-purple-700 hover:text-pink-600 transition-colors duration-300 font-semibold"
                >
                  {link.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
